/*
 * Behavior "tagged users" (CVX-6668): a behavior observation's `recipients` are
 * the users it is about. These predicates mirror the rest-api getObservations
 * selectors (recipientTeams / recipientTeamsAnyTeam / recipientSupervisors,
 * Corvex::Observations::resolveTaggedUsers) so client-side filtering of cached
 * observations agrees with the server.
 */

export interface ITaggedUserAccount {
  primaryGroup?: number;
  groups?: number[];
  supervisorID?: number;
}

export type AccountLookup = (userID: number) => ITaggedUserAccount | undefined;

/** The recipients of an observation as numbers (older rows hold string IDs). */
export function taggedUserIDs(obs: { recipients?: any[] }): number[] {
  return (Array.isArray(obs?.recipients) ? obs.recipients : []).map(Number).filter(id => id > 0);
}

/** A user's teams for matching: primary team (primaryGroup, else first group), or all of them. */
export function taggedUserTeams(account: ITaggedUserAccount | undefined, anyTeam: boolean): number[] {
  const groups = (account?.groups || []).map(Number);
  if (anyTeam) {
    return groups;
  }
  const primary = Number(account?.primaryGroup) || groups[0] || 0;
  return primary ? [primary] : [];
}

/** A tagged user's primary team (0 when they have none). */
export function taggedUserTeam(userID: number, lookup: AccountLookup): number {
  return taggedUserTeams(lookup(Number(userID)), false)[0] || 0;
}

/**
 * Report group-bys run once per tagged user; this is true only for the first
 * tagged user with a given key (team, supervisor), so an observation tagging two
 * users on one team lands in that team's bucket once.
 */
export function firstTaggedFor(obs: { recipients?: any[] }, userID: number, keyOf: (id: number) => any): boolean {
  const key = keyOf(Number(userID));
  return taggedUserIDs(obs).find(id => keyOf(id) === key) === Number(userID);
}

/** True when one of the observation's tagged users is in one of `teams`. */
export function tagsUserInTeams(obs: { recipients?: any[] }, teams: any[], anyTeam: boolean, lookup: AccountLookup): boolean {
  const wanted = new Set(teams.map(Number));
  return taggedUserIDs(obs).some(id => taggedUserTeams(lookup(id), anyTeam).some(t => wanted.has(t)));
}

/** True when one of the observation's tagged users reports to `supervisorID`. */
export function tagsReportOf(obs: { recipients?: any[] }, supervisorID: number, lookup: AccountLookup): boolean {
  return !!supervisorID && taggedUserIDs(obs).some(id => Number(lookup(id)?.supervisorID) === Number(supervisorID));
}
